<template>
  <UISideSheet
    :is-open="isPanelOpen"
    :title="panelTitle"
    :show-back="isViewingTopics"
    width="550px"
    full-height-mobile
    @toggle-side-sheet="tutorialsPanelStore.closePanel()"
    @back="selectedCategoryId = null"
    @closed="handleSheetClosed"
  >
    <div
      data-cy="tutorials-panel"
      class="tutorials-panel-content"
    >
      <TutorialsCategoryList
        v-if="!isViewingTopics"
        @select="selectedCategoryId = $event"
      />
      <TutorialsTopicList
        v-else
        :category-id="selectedCategoryId"
        @select-topic="handleTopicClick"
      />
    </div>
  </UISideSheet>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import {
  onboardingCategories,
  onboardingTopicContentTypes,
  onboardingWalkthroughs,
} from '@/constants/onboardingChecklist';
import { useTutorialsPanelStore } from '@/stores/useTutorialsPanelStore';
import { useWalkthroughLaunch } from '@/composables/useWalkthroughLaunch';

const tutorialsPanelStore = useTutorialsPanelStore();
const { isPanelOpen } = storeToRefs(tutorialsPanelStore);
const { launchWalkthrough } = useWalkthroughLaunch();

// Two views: the category list, or (once a category is picked) that category's topics.
const selectedCategoryId = ref(null);
const isViewingTopics = computed(() => Boolean(selectedCategoryId.value));

const selectedCategory = computed(() =>
  onboardingCategories.find((category) => category.id === selectedCategoryId.value),
);
const panelTitle = computed(() => selectedCategory.value?.name ?? 'Tutorials');

// Always re-enter on the category view, never mid-drill-down from a previous visit.
watch(isPanelOpen, (open) => {
  if (!open) {
    selectedCategoryId.value = null;
  }
});

const pendingWalkthrough = ref(null);
const pendingStaticTutorialId = ref(null);

const handleTopicClick = (topicId) => {
  const topic = onboardingWalkthroughs.find((candidate) => candidate.id === topicId);

  if (!topic) {
    return;
  }

  // The static modal is queued the same way a walkthrough is: both need this panel gone
  // first — the walkthrough to measure targets beneath it, the modal because the two
  // overlays share a z-index and the panel would paint over it.
  if (onboardingTopicContentTypes[topicId] === 'walkthrough') {
    pendingWalkthrough.value = topic;
  } else if (topic.slides?.length) {
    pendingStaticTutorialId.value = topicId;
  } else {
    return;
  }

  tutorialsPanelStore.closePanel();
  // Mobile only: MobileNavBar's slide-out menu sits underneath this panel and must also close
  tutorialsPanelStore.requestMobileMenuClose();
};

// `closed` fires on every close (X, overlay, back, a topic pick), not just ours — only
// act when something is actually queued.
const handleSheetClosed = () => {
  const walkthrough = pendingWalkthrough.value;
  const staticTutorialId = pendingStaticTutorialId.value;
  pendingWalkthrough.value = null;
  pendingStaticTutorialId.value = null;

  if (walkthrough) {
    launchWalkthrough(walkthrough, { source: 'tutorials-panel' });
  } else if (staticTutorialId) {
    tutorialsPanelStore.openStaticTutorial(staticTutorialId);
  }
};
</script>
